import { List } from 'immutable';
import React from 'react'
import { Link } from '../../../../../utils/link'
import { slugify } from '../../../../../utils/utils';
import { Centrum, Department } from '../../../cmsPageState';
import { ConsultationOverview, ConsultationTimes, KindOfWaitingTimes, WaitingTimesByKind } from './waitingTimesBlockState'

export const WaitingTimesBlockLayout = {
  AllLocations: [
    'Alsmeer',
    'Amsterdam',
    'Haarlem Noord',
    'Haarlem Zuid',
    'Heemstede',
    'Hillegom',
    'Hoofddorp',
    'IJmuiden',
    'Nieuw-Vennep',
    'Velsen Noord',
  ],
  MainLocations: List(['Haarlem Zuid', 'Hoofddorp', 'Haarlem Noord']),
  TableWrapper: (props: { children: JSX.Element | JSX.Element[], linkElement?: JSX.Element }) => (
    <section className="block block--table block--wt">
      <div className="block__inner container--m gutter--m spacing--wysiwyg">{props.children}</div>
      {props.linkElement ? props.linkElement : <></>}
    </section>
  ),
  Table: (props: { kind: KindOfWaitingTimes | 'Overzicht'; children: JSX.Element[] }) => (
    <section className="block__section">
      <header className="block__subheader">
        <h2 className={`block__subtitle title--l${props.kind == 'Overzicht' ? ' sr-text' : ''}`}>
          {props.kind == 'ConsultMetArts'
            ? 'Wachttijd voor afspraak op de polikliniek'
            : props.kind == 'Onderzoek'
            ? 'Wachttijd voor onderzoeken'
            : props.kind == 'Behandeling'
            ? 'Wachttijd voor behandelingen'
            : props.kind == 'Afspraaksoorten'
            ? 'Wachttijd voor afspraak op de polikliniek'
            : 'Wachttijden per afdeling'}
        </h2>
      </header>

      <div className={`block__table table table--wt-${props.kind}`}>
        <div className="table__head" aria-hidden="true">
          <div className="table__tr">
            <div className="table__th">
              {props.kind == 'ConsultMetArts'
                ? 'Locatie'
                : props.kind == 'Onderzoek'
                ? 'Onderzoek'
                : props.kind == 'Behandeling'
                ? 'Behandeling'
                : props.kind == 'Afspraaksoorten'
                ? 'Afspraaksoorten'
                : 'Afdeling'}
            </div>

            {/* All tables except sub-tables  */}
            {props.kind != 'ConsultMetArts' ? (
              WaitingTimesBlockLayout.MainLocations.toArray().map((location) => (
                <div className="table__th hide--tablet--m-down" key={location}>
                  {location}
                </div>
              ))
            ) : (
              <div className="table__th hide--tablet--m-down">Consult met arts</div>
            )}
          </div>
        </div>

        <ul className="table__body">{props.children}</ul>
      </div>
    </section>
  ),
  Row: (props: {
    kind: KindOfWaitingTimes | 'Overzicht'
    title: string
    link?: string
    waitingTimes: { location: string; time: number }[] | number
  }): JSX.Element => {
    return (
      <li className="table__tr" key={props.title}>
        <div className="table__td">
          <h3 className="weight--regular">
            {props.link ? (
              <Link url={props.link} className="link--styled">
                {props.title}
              </Link>
            ) : (
              props.title
            )}
          </h3>

          {/* Content for mobile */}
          <WaitingTimesBlockLayout.MobileContent kind={props.kind} waitingTimes={props.waitingTimes} />
        </div>

        {/* Content for desktop */}
        <WaitingTimesBlockLayout.DesktopContent kind={props.kind} waitingTimes={props.waitingTimes} />
      </li>
    )
  },
  MobileContent: (props: { 
    kind: KindOfWaitingTimes | 'Overzicht', 
    waitingTimes: {location: string, time: number}[] | number
  }) => (
    props.kind != 'ConsultMetArts' && typeof props.waitingTimes != 'number' ? (
      <ul className="sr-text--tablet--m-up">
        {props.waitingTimes.map((wt) => (
          <li key={`${wt.location}-${wt.time}`}>
            {wt.location}: <WaitingTimesBlockLayout.Day waittime={wt.time} />
          </li>
        ))}
      </ul>
    ) : (
      <p className="sr-text--tablet--m-up">
        {typeof props.waitingTimes == 'number' && <WaitingTimesBlockLayout.Day waittime={props.waitingTimes} />}
      </p>
    )
  ),
  DesktopContent: (props: { 
    kind: KindOfWaitingTimes | 'Overzicht'
    waitingTimes: {location: string, time: number}[] | number
  }): JSX.Element => (
    props.kind != 'ConsultMetArts' && typeof props.waitingTimes != 'number' ? (
      <>
        {props.waitingTimes.map((wt) => (
          <div className="table__td hide--tablet--m-down" aria-hidden="true" key={`${wt.location}-${wt.time}`}>
            <WaitingTimesBlockLayout.Day waittime={wt.time} />
          </div>
        ))}
      </>
    ) : (
      <div className="table__td hide--tablet--m-down" aria-hidden="true">
        {typeof props.waitingTimes == 'number' && <WaitingTimesBlockLayout.Day waittime={props.waitingTimes} />}
      </div>
    )
  ),
  Link: (props: { title: string; url: string }) => {
    return (
      <div className="container--m gutter--m text--styled">
        <Link url={props.url}>Meer informatie over {props.title}</Link>
      </div>
    )
  },
  Day: (props: { waittime: number | '-' }): JSX.Element => (
    props.waittime == '-' || props.waittime <= 0 ? (
      <abbr title="Niet beschikbaar">-</abbr>
    ) : (
      <>{props.waittime + (props.waittime === 1 ? ` dag` : ` dagen`)}</>
    )
  ),
  Prefix: (props: { text: string, spoedafspraak?: string }) => (
    <>
      <div className="container--m gutter--m text--styled">
        <div className="block__content--s" dangerouslySetInnerHTML={{ __html: props.text }} />
      </div>
      {props.spoedafspraak && (
        <div className="spacing--m container--m gutter--m text--styled">
          <div
            className="block__content--s section--bg-tertiary"
            dangerouslySetInnerHTML={{ __html: props.spoedafspraak }}
          />
        </div>
      )}
    </>
  ),
  Suffix: (props: { text: string }) => (
    <>
      <div className="container--m gutter--m text--styled">
        <div className="block__content--s" dangerouslySetInnerHTML={{ __html: props.text }} />
      </div>
    </>
  ),
  ParentLink: (props: { parent: Department | Centrum }) => (
    <WaitingTimesBlockLayout.Link title={props.parent.title} url={`/afdelingen-en-centra/${slugify(props.parent.title)}`}/>
  ),
  ConsultationOverview: (props: { overview: ConsultationOverview }) => (
    <WaitingTimesBlockLayout.TableWrapper>
      <WaitingTimesBlockLayout.Table kind="Overzicht">
        {props.overview.map(wt => {
          const rowTitle = wt.department ? wt.department.title : wt.centrum ? wt.centrum.title : ''
          const rowUrl = wt.department ? `/wachttijden?department=${wt.department.title}` : wt.centrum ? `/wachttijden?centrum=${wt.centrum.title}` : ''
          return <WaitingTimesBlockLayout.Row
            kind="Overzicht"
            title={rowTitle}
            link={rowUrl}
            waitingTimes={wt.locations}
          />
        })}
      </WaitingTimesBlockLayout.Table>
    </WaitingTimesBlockLayout.TableWrapper>
  ),
  ConsultationTimes: (props: { 
    consultations: ConsultationTimes
    linkElement?: JSX.Element 
  }) => (
    <WaitingTimesBlockLayout.TableWrapper
      linkElement={props.linkElement}
    >
      <WaitingTimesBlockLayout.Table kind="ConsultMetArts">
        {props.consultations.map(wt => (
          <WaitingTimesBlockLayout.Row
            kind="ConsultMetArts"
            title={wt.location}
            link={undefined}
            waitingTimes={wt.time}
          />
        ))}
      </WaitingTimesBlockLayout.Table>
    </WaitingTimesBlockLayout.TableWrapper>
  ),
  CentraLinkL: (props: {centrum: Centrum})=> {
    if(props.centrum.appointmentUrl && props.centrum.appointmentUrl != ""){
      return (
        <div className="container--m gutter--m text--styled">
          <Link url={props.centrum.appointmentUrl}>{props.centrum.title}</Link>
        </div>
      )
    }
    return <p>{props.centrum.title}</p>
  },
  WaitingTimesByDepartment: (props: {
    waitingTimes: WaitingTimesByKind
    linkElement?: JSX.Element 
  }) => (
    <WaitingTimesBlockLayout.TableWrapper
      linkElement={props.linkElement}
    >
      {props.waitingTimes.map(
        (wtPerKind): JSX.Element => (
          <WaitingTimesBlockLayout.Table kind={wtPerKind.kind}>
            {wtPerKind.waitingTimes.map(wt => (
              <WaitingTimesBlockLayout.Row
                kind={wtPerKind.kind}
                title={wt.name}
                link={undefined}
                waitingTimes={wt.locations}
              />
            ))}
          </WaitingTimesBlockLayout.Table>
        )
      )}
    </WaitingTimesBlockLayout.TableWrapper>
  )
}
